import type { TaggedRiskRow } from "@/lib/api/riskV3";
import { formatNumberToDisplay } from "@/lib/api/common";

// ── Helpers ────────────────────────────────────────────────────────

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-medium text-muted-foreground leading-tight">{label}</span>
      <span className="text-xs leading-tight">{value}</span>
    </div>
  );
}

function fmt(val: string | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return val;
}

// ── BrokerageCard ──────────────────────────────────────────────────

interface BrokerageCardProps {
  row: TaggedRiskRow;
  customerNameMap: Record<string, string>;
}

export function BrokerageCard({ row, customerNameMap }: BrokerageCardProps) {
  const pos = row.position_data;
  const customerName = row.customer_id ? customerNameMap[row.customer_id] ?? row.customer_id : "—";

  const putOrCall = pos?.put_or_call === "P" ? "Put" : pos?.put_or_call === "C" ? "Call" : "Future";
  const status = pos?.enabled ? "Open" : "Closed";

  return (
    <div className="rounded-lg border bg-white p-3 shadow-sm space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold">
          {fmt(pos?.contract_description)}
        </span>
        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-medium">
          {putOrCall}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {status}
        </span>
        {pos?.option_month && (
          <span className="text-[10px] text-muted-foreground">{pos.option_month}</span>
        )}
      </div>

      {/* Main fields */}
      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <Field label="Customer" value={customerName} />
        <Field label="Commodity" value={fmt(row.meta.commodity_display_name)} />
        <Field label="Trading Account" value={fmt(pos?.trading_account)} />
        <Field label="Crop Year" value={String(row.crop_year)} />
      </div>

      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <Field label="Long Qty" value={String(pos?.long_qty ?? 0)} />
        <Field label="Short Qty" value={String(pos?.short_qty ?? 0)} />
        <Field label="Quantity" value={formatNumberToDisplay(row.quantity)} />
        <Field label="Price" value={formatNumberToDisplay(row.price)} />
      </div>

      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <Field label="Strike Price" value={formatNumberToDisplay(pos?.strike_price)} />
        <Field label="Premium" value={formatNumberToDisplay(pos?.premium)} />
        <Field label="Provider" value={fmt(row.meta.brokerage_provider_type)} />
        <Field label="Type" value={fmt(row.meta.brokerage_type)} />
      </div>
    </div>
  );
}
